import { Button, cn } from "@win-reality/react-ui";

import hitterDark from "@/assets/tagging/players/hitter-dark.svg";
import hitterWhite from "@/assets/tagging/players/hitter-white.svg";
import pitcherDark from "@/assets/tagging/players/pitcher-dark.svg";
import pitcherWhite from "@/assets/tagging/players/pitcher-white.svg";

import { useTaggingTheme } from "../../hooks/useTaggingTheme";
import { OUTLINE_BUTTON_COLOR_CLASS } from "../sidebarButton";

const ROLE_ICONS = {
  Hitter: { light: hitterDark, dark: hitterWhite },
  Pitcher: { light: pitcherDark, dark: pitcherWhite },
};

export type PlayerCardAction = {
  label: string;
  /** Omit for actions that aren't built yet */
  onClick?: () => void;
  isDisabled?: boolean;
  /** Shown on hover, e.g. why the action is disabled */
  title?: string;
  /** Test hook: literal id for this action's button */
  testId?: string;
};

type PlayerCardProps = {
  playerRole: "Hitter" | "Pitcher";
  name: string;
  jerseyNumber?: string;
  teamColor: string;
  /** Hitter only */
  lineupPosition?: number;
  actions: PlayerCardAction[];
  /** Test hooks: literal id from the call site and the card's instance attributes */
  "data-testid"?: string;
  "data-player-role"?: "hitter" | "pitcher";
  "data-player-id"?: string;
};

/** Hitter/pitcher card in the Post At-Bat editor, edged in the team color. */
export const PlayerCard = ({
  playerRole,
  name,
  jerseyNumber,
  teamColor,
  lineupPosition,
  actions,
  "data-testid": testId,
  "data-player-role": playerRoleAttr,
  "data-player-id": playerId,
}: PlayerCardProps) => {
  const { theme } = useTaggingTheme();

  return (
    <div
      data-testid={testId}
      data-player-role={playerRoleAttr}
      data-player-id={playerId}
      className={cn(
        // Spacing
        "space-y-4 px-4 py-4",
        // Border
        "rounded-md border-l-2",
        // Background
        "bg-tagging-option",
      )}
      style={{ borderLeftColor: teamColor }}
    >
      <div className="flex items-center gap-4">
        <div className="flex w-10 shrink-0 flex-col items-center gap-0.5 text-gray-900 dark:text-white">
          <img
            src={ROLE_ICONS[playerRole][theme]}
            alt=""
            className="h-5 w-auto"
          />
          <span className="text-[10px]">{playerRole}</span>
        </div>
        <div className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-white">
          {jerseyNumber && <span className="mr-2">#{jerseyNumber}</span>}
          {name}
        </div>
        {lineupPosition != null && (
          <div className="flex shrink-0 items-center gap-3">
            <span className="text-[10px] text-gray-900 dark:text-white">
              Lineup position
            </span>
            <span
              className="flex h-6 w-6 items-center justify-center rounded-[3px] text-xs font-bold text-black"
              style={{ backgroundColor: teamColor }}
            >
              {lineupPosition}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        {actions.map((action) => (
          <Button
            key={action.label}
            variant="secondary-flat-dark-outline"
            size="sm"
            onClick={action.onClick}
            isDisabled={action.isDisabled}
            title={action.title}
            data-testid={action.testId}
            data-player-role={playerRoleAttr}
            className={cn("rounded-full", OUTLINE_BUTTON_COLOR_CLASS)}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default PlayerCard;
